import * as React from 'react';
import {
  Box,
  Modal,
  Typography,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  Grid,
  Paper,
  Button,
  DialogActions,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import { statesList } from 'views/dashboard/private/Banking/AccountRegister/components/AddressForm';

import { BankingService } from 'services';
import { IBeneficiary } from 'common/beneficiary.interface';
import { cnpjFormatter, cpfFormatter } from 'utils/formatters';
import { formatCurrency, parseCurrency } from 'utils/banking';

import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { ICreateBankSlipRequest } from 'types/banking';
import { camelToSnakeCase } from 'utils/api';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 720,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: '12px',
  boxShadow: '0px 10px 30px rgba(0,0,0,0.15)',
  p: 0,
  display: 'flex',
  flexDirection: 'column',
};

type HeaderStep = 'informar' | 'selecionar';

const StepHeader = ({ step, onChangeStep }: { step: HeaderStep; onChangeStep: (s: HeaderStep) => void }) => (
  <Box px={4} pt={3} pb={2}>
    <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={2}>
      <QrCode2Icon sx={{ color: 'primary.main' }} />
      <Typography fontWeight={600}>Emitir boleto individual</Typography>
    </Box>

    <Box display="flex" bgcolor="grey.100" borderRadius={2} p={1} width="100%" gap={1}>
      <Box
        onClick={() => onChangeStep('informar')}
        role="button"
        tabIndex={0}
        sx={{
          flex: '0 0 50%',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 40,
          bgcolor: step === 'informar' ? 'background.paper' : 'transparent',
          cursor: 'pointer',
        }}
      >
        <Typography fontSize={13} fontWeight={500} lineHeight="16px" color={step === 'informar' ? 'text.primary' : 'text.secondary'}>
          Informar dados
        </Typography>
      </Box>

      <Box
        onClick={() => onChangeStep('selecionar')}
        role="button"
        tabIndex={0}
        sx={{
          flex: '1 1 auto',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 40,
          bgcolor: step === 'selecionar' ? 'background.paper' : 'transparent',
          cursor: 'pointer',
        }}
      >
        <Typography fontSize={13} fontWeight={500} lineHeight="16px" color={step === 'selecionar' ? 'text.primary' : 'text.secondary'}>
          Selecionar pagador
        </Typography>
      </Box>
    </Box>
  </Box>
);

const Row = ({ name, taxNumber, onClick }: { name: string; taxNumber?: string | null; onClick?: () => void }) => {
  const getFormattedTaxNumber = () => {
    if (!taxNumber) return '-';
    return taxNumber.length > 11 ? cnpjFormatter(taxNumber).maskedValue : cpfFormatter(taxNumber).maskedValue;
  };

  return (
    <Box
      px={4}
      py={2}
      display="flex"
      justifyContent="space-between"
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '8px', cursor: 'pointer' }}
      onClick={onClick}
    >
      <Box>
        <Typography fontWeight={500}>{name}</Typography>
        <Typography fontSize={12} color="text.secondary">
          CNPJ/CPF: {getFormattedTaxNumber()}
        </Typography>
      </Box>

      <Box textAlign="right">
        <Typography fontSize={12} color="text.secondary" mb={0.5}>
          Configuração personalizada
        </Typography>
      </Box>
    </Box>
  );
};

const validationSchema = Yup.object().shape({
  amount: Yup.string().required('Campo obrigatório'),
  dueDate: Yup.string().required('Campo obrigatório'),
  description: Yup.string().max(100, 'Máximo 100 caracteres'),

  allowLatePayment: Yup.boolean(),
  lateFinePercent: Yup.string().when('allowLatePayment', {
    is: true,
    then: Yup.string().required('Obrigatório'),
  }),
  lateMonthlyInterestPercent: Yup.string().when('allowLatePayment', {
    is: true,
    then: Yup.string().required('Obrigatório'),
  }),
  lateDays: Yup.string().when('allowLatePayment', {
    is: true,
    then: Yup.string().required('Obrigatório'),
  }),

  allowEarlyDiscount: Yup.boolean(),
  earlyDiscountType: Yup.string().when('allowEarlyDiscount', {
    is: true,
    then: Yup.string().required('Obrigatório'),
  }),
  earlyDiscountValue: Yup.string().when('allowEarlyDiscount', {
    is: true,
    then: Yup.string().required('Obrigatório'),
  }),
  earlyDiscountDays: Yup.string().when('allowEarlyDiscount', {
    is: true,
    then: Yup.string().required('Obrigatório'),
  }),

  payerName: Yup.string().required('Obrigatório'),
  payerDocument: Yup.string().required('Obrigatório'),

  zipCode: Yup.string().required('Obrigatório'),
  street: Yup.string().required('Obrigatório'),
  number: Yup.string().required('Obrigatório'),
  neighborhood: Yup.string().required('Obrigatório'),
  city: Yup.string().required('Obrigatório'),
  state: Yup.string().required('Obrigatório'),
});

const bankingService = new BankingService();

const Boleto = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [step, setStep] = React.useState<HeaderStep>('selecionar');
  const [selectedBeneficiary, setSelectedBeneficiary] = React.useState<IBeneficiary | null>(null);
  const [beneficiaries, setBeneficiaries] = React.useState<IBeneficiary[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');

  const fetchBeneficiaries = async () => {
    setLoading(true);
    try {
      const response = await bankingService.getBeneficiaries({
        page: 1,
        limit: 100,
        counterpartyType: 'BOLETO_ISSUANCE',
      } as any);
      setBeneficiaries(response.items);
    } catch (error) {
      enqueueSnackbar('Erro ao buscar beneficiários', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAddressFromViaCep = async (cep: string) => {
    try {
      // Remove non-numeric characters
      const cleanCep = cep.replace(/\D/g, '');

      // Check if CEP has 8 digits
      if (cleanCep.length !== 8) {
        return;
      }

      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();

      // Check if ViaCEP returned an error
      if (data.erro) {
        return;
      }

      // Auto-fill the address fields
      formik.setValues({
        ...formik.values,
        zipCode: cep,
        street: data.logradouro || formik.values.street,
        neighborhood: data.bairro || formik.values.neighborhood,
        city: data.localidade || formik.values.city,
        state: data.uf || formik.values.state,
      });
    } catch (error) {
      // Silently fail - if ViaCEP fails, simply do not fill
    }
  };

  const handleZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    formik.handleChange(e);

    // Trigger ViaCEP fetch when CEP has 8 digits (including the hyphen format: 00000-000)
    const cleanCep = value.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      fetchAddressFromViaCep(value);
    }
  };

  React.useEffect(() => {
    if (open) {
      fetchBeneficiaries();
    } else {
      setSelectedBeneficiary(null);
      setStep('selecionar');
      setSearchTerm('');
    }
  }, [open]);

  const filteredBeneficiaries = React.useMemo(() => {
    if (!searchTerm) return beneficiaries;
    const term = searchTerm.toLowerCase();
    return beneficiaries.filter((b) => (b.businessName || '').toLowerCase().includes(term) || (b.taxNumber && b.taxNumber.includes(term)));
  }, [beneficiaries, searchTerm]);

  React.useEffect(() => {
    if (selectedBeneficiary) {
      formik.setValues({
        ...formik.values,
        payerName: selectedBeneficiary.businessName || '',
        payerDocument: selectedBeneficiary.taxNumber || '',
        zipCode: selectedBeneficiary.postalCode || '',
        street: selectedBeneficiary.street || '',
        number: selectedBeneficiary.streetNumber || '',
        complement: selectedBeneficiary.addressComplement || '',
        neighborhood: selectedBeneficiary.neighborhood || '',
        city: selectedBeneficiary.city || '',
        state: selectedBeneficiary.state || '',
        allowLatePayment: !!selectedBeneficiary.allowLatePayment,
        lateFinePercent: selectedBeneficiary.lateFeePercentage ? String(selectedBeneficiary.lateFeePercentage) : '',
        lateMonthlyInterestPercent: selectedBeneficiary.latePaymentInterestRate ? String(selectedBeneficiary.latePaymentInterestRate) : '',
        lateDays: selectedBeneficiary.latePaymentGraceDays ? String(selectedBeneficiary.latePaymentGraceDays) : '',
        allowEarlyDiscount: !!selectedBeneficiary.earlyPaymentDiscountEnabled,
        earlyDiscountType: (() => {
          if (selectedBeneficiary.earlyPaymentConditionType === 'FIXED') return 'fixo';
          if (selectedBeneficiary.earlyPaymentConditionType === 'PERCENT') return 'percentual';
          return '';
        })(),
        earlyDiscountValue: selectedBeneficiary.earlyDiscountValue ? String(selectedBeneficiary.earlyDiscountValue) : '',
        earlyDiscountDays: selectedBeneficiary.earlyPaymentDiscountDays ? String(selectedBeneficiary.earlyPaymentDiscountDays) : '',
      });
    }
  }, [selectedBeneficiary]);

  const formik = useFormik({
    initialValues: {
      amount: '',
      dueDate: '',
      description: '',

      payerName: '',
      payerDocument: '',

      zipCode: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',

      allowLatePayment: false,
      lateFinePercent: '',
      lateMonthlyInterestPercent: '',
      lateDays: '',

      allowEarlyDiscount: false,
      earlyDiscountType: '',
      earlyDiscountValue: '',
      earlyDiscountDays: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        let debtor;

        if (selectedBeneficiary) {
          debtor = {
            name: selectedBeneficiary.businessName,
            document: selectedBeneficiary.taxNumber || '000.000.000-00', // API requires document
            postalCode: selectedBeneficiary.postalCode || '-',
            publicArea: selectedBeneficiary.street || '-',
            number: selectedBeneficiary.streetNumber || '0',
            neighborhood: selectedBeneficiary.neighborhood || '-',
            city: selectedBeneficiary.city || '-',
            state: selectedBeneficiary.state || '-',
          };
        } else {
          debtor = {
            name: values.payerName,
            document: values.payerDocument,
            postalCode: values.zipCode,
            publicArea: values.street,
            number: values.number,
            neighborhood: values.neighborhood,
            city: values.city,
            state: values.state,
          };
        }

        const payload: ICreateBankSlipRequest = {
          amount: parseCurrency(values.amount),
          duedate: values.dueDate,
          debtor,
          instructions: {
            ...(values.allowLatePayment && {
              fine: {
                value: Number(values.lateFinePercent),
                type: 'PERCENT',
              },
              interest: {
                value: Number(values.lateMonthlyInterestPercent),
                type: 'PERCENT',
              },
            }),
            ...(values.allowEarlyDiscount && {
              discounts: [
                camelToSnakeCase({
                  value: Number(values.earlyDiscountValue),
                  type: values.earlyDiscountType === 'percentual' ? 'PERCENT' : 'FIXED',
                  limitDate: values.dueDate,
                }),
              ],
            }),
          },
        };

        await bankingService.createBankSlip(payload);
        enqueueSnackbar('Boleto emitido com sucesso', { variant: 'success' });
        onClose();
      } catch (error) {
        enqueueSnackbar('Erro ao emitir boleto', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
  });

  let body: React.ReactNode;
  if (step === 'informar') {
    body = (
      <Box>
        <Box display="flex" alignItems="center">
          <PersonOutlineIcon fontSize="large" sx={{ mr: 1, color: 'text.primary' }} />
          <Typography sx={{ fontSize: '22px', color: 'text.primary' }}>Dados do pagador</Typography>
        </Box>

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Nome *"
              placeholder="Nome do pagador"
              variant="standard"
              fullWidth
              name="payerName"
              value={formik.values.payerName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.payerName && Boolean(formik.errors.payerName)}
              helperText={formik.touched.payerName && formik.errors.payerName}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="CPF/CNPJ *"
              placeholder="000.000.000-00"
              variant="standard"
              fullWidth
              name="payerDocument"
              value={formik.values.payerDocument}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.payerDocument && Boolean(formik.errors.payerDocument)}
              helperText={formik.touched.payerDocument && formik.errors.payerDocument}
            />
          </Grid>
        </Grid>

        <Box display="flex" alignItems="center">
          <MapsHomeWorkOutlinedIcon fontSize="large" sx={{ mr: 1, color: 'text.primary' }} />
          <Typography sx={{ fontSize: '22px', color: 'text.primary' }}>Endereço do pagador</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <TextField
              label="CEP *"
              placeholder="00000-000"
              variant="standard"
              fullWidth
              name="zipCode"
              value={formik.values.zipCode}
              onChange={handleZipCodeChange}
              onBlur={formik.handleBlur}
              error={formik.touched.zipCode && Boolean(formik.errors.zipCode)}
              helperText={formik.touched.zipCode && formik.errors.zipCode}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              label="Rua *"
              placeholder="Nome da rua"
              variant="standard"
              fullWidth
              name="street"
              value={formik.values.street}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.street && Boolean(formik.errors.street)}
              helperText={formik.touched.street && formik.errors.street}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Número *"
              placeholder="Número do endereço"
              variant="standard"
              fullWidth
              name="number"
              value={formik.values.number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.number && Boolean(formik.errors.number)}
              helperText={formik.touched.number && formik.errors.number}
            />
          </Grid>

          <Grid item xs={12} md={8}>
            <TextField
              label="Complemento (opcional)"
              placeholder="Apto, sala, bloco..."
              variant="standard"
              fullWidth
              name="complement"
              value={formik.values.complement}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.complement && Boolean(formik.errors.complement)}
              helperText={formik.touched.complement && formik.errors.complement}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Bairro *"
              placeholder="Nome do bairro"
              variant="standard"
              fullWidth
              name="neighborhood"
              value={formik.values.neighborhood}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.neighborhood && Boolean(formik.errors.neighborhood)}
              helperText={formik.touched.neighborhood && formik.errors.neighborhood}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Cidade *"
              placeholder="Nome da cidade"
              variant="standard"
              fullWidth
              name="city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Estado *"
              placeholder="UF"
              variant="standard"
              fullWidth
              name="state"
              value={formik.values.state}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.state && Boolean(formik.errors.state)}
              helperText={formik.touched.state && formik.errors.state}
            >
              {statesList.map((uf) => (
                <MenuItem key={uf} value={uf}>
                  {uf}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Box display="flex" alignItems="center" mt={3}>
          <QrCode2Icon fontSize="large" sx={{ mr: 1, color: 'text.primary' }} />
          <Typography sx={{ fontSize: '22px', color: 'text.primary' }}>Dados do Boleto</Typography>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Vencimento *"
              placeholder="Selecione a data"
              variant="standard"
              fullWidth
              name="dueDate"
              type="date"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              value={formik.values.dueDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.dueDate && Boolean(formik.errors.dueDate)}
              helperText={formik.touched.dueDate && formik.errors.dueDate}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Valor da cobrança (R$) *"
              placeholder="R$ 0,00"
              variant="standard"
              fullWidth
              name="amount"
              value={formik.values.amount}
              onChange={(e) => formik.setFieldValue('amount', formatCurrency(e.target.value))}
              onBlur={formik.handleBlur}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição (Opcional)"
              placeholder="Ex: Pagamento de serviços, mensalidade..."
              variant="standard"
              fullWidth
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </Grid>
        </Grid>

        <FormControlLabel
          control={
            <Switch checked={formik.values.allowLatePayment} onChange={(e, checked) => formik.setFieldValue('allowLatePayment', checked)} />
          }
          label="Permitir pagamento em atraso"
          sx={{ mb: 2, display: 'block', width: '100%', mt: 2 }}
        />

        {formik.values.allowLatePayment && (
          <>
            <Typography variant="subtitle2">Encargos após o vencimento</Typography>

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Multa (%)"
                  type="number"
                  fullWidth
                  required
                  variant="standard"
                  name="lateFinePercent"
                  value={formik.values.lateFinePercent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lateFinePercent && Boolean(formik.errors.lateFinePercent)}
                  helperText={formik.touched.lateFinePercent && formik.errors.lateFinePercent}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Juros ao mês (%)"
                  type="number"
                  fullWidth
                  required
                  variant="standard"
                  name="lateMonthlyInterestPercent"
                  value={formik.values.lateMonthlyInterestPercent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lateMonthlyInterestPercent && Boolean(formik.errors.lateMonthlyInterestPercent)}
                  helperText={formik.touched.lateMonthlyInterestPercent && formik.errors.lateMonthlyInterestPercent}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Quantidade de dias"
                  type="number"
                  fullWidth
                  required
                  variant="standard"
                  name="lateDays"
                  value={formik.values.lateDays}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lateDays && Boolean(formik.errors.lateDays)}
                  helperText={formik.touched.lateDays && formik.errors.lateDays}
                />
              </Grid>
            </Grid>
          </>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={formik.values.allowEarlyDiscount}
              onChange={(e, checked) => formik.setFieldValue('allowEarlyDiscount', checked)}
            />
          }
          label="Desconto para pagamento antecipado"
          sx={{ mb: 2, display: 'block', width: '100%' }}
        />

        {formik.values.allowEarlyDiscount && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl
                fullWidth
                required
                variant="standard"
                error={Boolean(formik.touched.earlyDiscountType && formik.errors.earlyDiscountType)}
              >
                <InputLabel>Tipo de desconto</InputLabel>
                <Select
                  label="Tipo de desconto"
                  name="earlyDiscountType"
                  value={formik.values.earlyDiscountType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="fixo">Valor fixo</MenuItem>
                  <MenuItem value="percentual">Percentual</MenuItem>
                </Select>
                <FormHelperText>{formik.touched.earlyDiscountType && formik.errors.earlyDiscountType}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Valor (R$)"
                type="number"
                placeholder="Valor do desconto"
                fullWidth
                required
                variant="standard"
                name="earlyDiscountValue"
                value={formik.values.earlyDiscountValue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.earlyDiscountValue && Boolean(formik.errors.earlyDiscountValue)}
                helperText={formik.touched.earlyDiscountValue && formik.errors.earlyDiscountValue}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Quantidade de dias"
                type="number"
                fullWidth
                required
                variant="standard"
                name="earlyDiscountDays"
                value={formik.values.earlyDiscountDays}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.earlyDiscountDays && Boolean(formik.errors.earlyDiscountDays)}
                helperText={formik.touched.earlyDiscountDays && formik.errors.earlyDiscountDays}
              />
            </Grid>
          </Grid>
        )}

        <DialogActions sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%' }}>
            <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 6, px: 4, flex: 1 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={loading || !(formik.isValid && formik.dirty)}
              sx={{
                borderRadius: 6,
                px: 4,
                backgroundColor: formik.isValid && formik.dirty && !loading ? 'primary.main' : '#E0E0E0',
                flex: 1,
              }}
              onClick={() => formik.handleSubmit()}
            >
              {loading && <CircularProgress size={24} sx={{ mr: 1 }} />}
              Emitir boleto
            </Button>
          </Box>
        </DialogActions>
      </Box>
    );
  } else if (!selectedBeneficiary) {
    body = (
      <>
        <TextField
          fullWidth
          placeholder="Buscar favorecido"
          variant="standard"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" flexDirection="column" gap={1.5} mt={2}>
          {loading && <Typography>Carregando...</Typography>}
          {!loading &&
            filteredBeneficiaries.map((b) => (
              <Row key={b.id} name={b.businessName || ''} taxNumber={b.taxNumber} onClick={() => setSelectedBeneficiary(b)} />
            ))}
          {!loading && filteredBeneficiaries.length === 0 && (
            <Typography color="text.secondary" align="center" mt={2}>
              Nenhum favorecido encontrado
            </Typography>
          )}
        </Box>
      </>
    );
  } else {
    body = (
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            bgcolor: 'background.default',
            borderRadius: 2,
          }}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <PersonOutlineIcon fontSize="large" sx={{ mr: 1, color: 'text.primary' }} />
            <Typography variant="h6" sx={{ fontSize: '22px', color: 'text.primary' }}>
              Dados do pagador
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" fontWeight={600}>
                Nome
              </Typography>
              <Typography>{selectedBeneficiary.businessName}</Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" fontWeight={600}>
                CPF/CNPJ
              </Typography>
              <Typography>{selectedBeneficiary.taxNumber || '-'}</Typography>
            </Grid>
          </Grid>

          <Box display="flex" alignItems="center" mb={2} mt={5}>
            <HomeOutlinedIcon fontSize="large" sx={{ mr: 1, color: 'text.primary' }} />
            <Typography variant="h6" sx={{ fontSize: '22px', color: 'text.primary' }}>
              Endereço do pagador
            </Typography>
          </Box>

          <Typography variant="caption" fontWeight={600}>
            Endereço
          </Typography>
          <Typography>{selectedBeneficiary.street || '-'}</Typography>
          <Typography>
            {selectedBeneficiary.city} - {selectedBeneficiary.state} - CEP: {selectedBeneficiary.postalCode}
          </Typography>

          {/* Removed boleto configuration section. Values are now pre-filled in the form fields. */}
        </Paper>

        <Box mb={2} display="flex" alignItems="center">
          <QrCode2Icon sx={{ mr: 1, color: 'text.secondary' }} />
          <Typography variant="h6" sx={{ fontSize: '22px' }}>
            Dados do Boleto
          </Typography>
        </Box>

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Typography fontWeight={500} mb={1}>
              Vencimento *
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Selecione a data"
              name="dueDate"
              type="date"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
              value={formik.values.dueDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.dueDate && Boolean(formik.errors.dueDate)}
              helperText={formik.touched.dueDate && formik.errors.dueDate}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography fontWeight={500} mb={1}>
              Valor da cobrança (R$) *
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              placeholder="R$ 0,00"
              name="amount"
              value={formik.values.amount}
              onChange={(e) => formik.setFieldValue('amount', formatCurrency(e.target.value))}
              onBlur={formik.handleBlur}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight={500} mb={1}>
              Descrição (Opcional)
            </Typography>
            <TextField
              fullWidth
              variant="standard"
              placeholder="Ex: Pagamento de serviços, mensalidade..."
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.description && Boolean(formik.errors.description)}
              helperText={formik.touched.description && formik.errors.description}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <Alert
          severity="info"
          sx={{
            alignItems: 'center',
            bgcolor: '#F2F4F8',
            color: 'primary.main',
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'primary.main',
              alignSelf: 'center',
            },
          }}
        >
          Você pode mudar as informações abaixo para a emissão deste boleto.
          <br />
          Caso queira mudar para as próximas emissões também, atualize essa informação no menu cadastro e contrapartes.
        </Alert>

        <FormControlLabel
          control={
            <Switch checked={formik.values.allowLatePayment} onChange={(e, checked) => formik.setFieldValue('allowLatePayment', checked)} />
          }
          label="Permitir pagamento em atraso"
          sx={{ mb: 2, display: 'block', width: '100%', mt: 2 }}
        />

        {formik.values.allowLatePayment && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Encargos após o vencimento
            </Typography>

            <Grid container spacing={2} mb={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Multa (%)"
                  type="number"
                  fullWidth
                  required
                  variant="standard"
                  name="lateFinePercent"
                  value={formik.values.lateFinePercent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lateFinePercent && Boolean(formik.errors.lateFinePercent)}
                  helperText={formik.touched.lateFinePercent && formik.errors.lateFinePercent}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Juros ao mês (%)"
                  type="number"
                  fullWidth
                  required
                  variant="standard"
                  name="lateMonthlyInterestPercent"
                  value={formik.values.lateMonthlyInterestPercent}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lateMonthlyInterestPercent && Boolean(formik.errors.lateMonthlyInterestPercent)}
                  helperText={formik.touched.lateMonthlyInterestPercent && formik.errors.lateMonthlyInterestPercent}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  label="Quantidade de dias"
                  type="number"
                  fullWidth
                  required
                  variant="standard"
                  name="lateDays"
                  value={formik.values.lateDays}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lateDays && Boolean(formik.errors.lateDays)}
                  helperText={formik.touched.lateDays && formik.errors.lateDays}
                />
              </Grid>
            </Grid>
          </>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={formik.values.allowEarlyDiscount}
              onChange={(e, checked) => formik.setFieldValue('allowEarlyDiscount', checked)}
            />
          }
          label="Desconto para pagamento antecipado"
          sx={{ mb: 2, display: 'block', width: '100%' }}
        />

        {formik.values.allowEarlyDiscount && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl
                fullWidth
                required
                variant="standard"
                error={Boolean(formik.touched.earlyDiscountType && formik.errors.earlyDiscountType)}
              >
                <InputLabel>Tipo de desconto</InputLabel>
                <Select
                  label="Tipo de desconto"
                  name="earlyDiscountType"
                  value={formik.values.earlyDiscountType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="fixo">Valor fixo</MenuItem>
                  <MenuItem value="percentual">Percentual</MenuItem>
                </Select>
                <FormHelperText>{formik.touched.earlyDiscountType && formik.errors.earlyDiscountType}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Valor (R$)"
                type="number"
                placeholder="Valor do desconto"
                fullWidth
                required
                variant="standard"
                name="earlyDiscountValue"
                value={formik.values.earlyDiscountValue}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.earlyDiscountValue && Boolean(formik.errors.earlyDiscountValue)}
                helperText={formik.touched.earlyDiscountValue && formik.errors.earlyDiscountValue}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="Quantidade de dias"
                type="number"
                fullWidth
                required
                variant="standard"
                name="earlyDiscountDays"
                value={formik.values.earlyDiscountDays}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.earlyDiscountDays && Boolean(formik.errors.earlyDiscountDays)}
                helperText={formik.touched.earlyDiscountDays && formik.errors.earlyDiscountDays}
              />
            </Grid>
          </Grid>
        )}

        <DialogActions sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, width: '100%' }}>
            <Button disabled={loading} variant="outlined" onClick={onClose} sx={{ borderRadius: 6, px: 4, flex: 1 }}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={loading || !(formik.isValid && formik.dirty)}
              sx={{
                borderRadius: 6,
                px: 4,
                backgroundColor: formik.isValid && formik.dirty ? 'primary.main' : '#E0E0E0',
                flex: 1,
              }}
              onClick={() => formik.handleSubmit()}
            >
              {loading && <CircularProgress size={24} sx={{ mr: 1 }} />}
              Emitir boleto
            </Button>
          </Box>
        </DialogActions>
      </Box>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <StepHeader step={step} onChangeStep={setStep} />

        <Box px={4} pb={2} sx={{ overflowY: 'auto', flex: '1 1 auto' }}>
          {body}
        </Box>

        <Box height={16} />
      </Box>
    </Modal>
  );
};

export default Boleto;
